import { Component, ElementRef, EventEmitter, HostListener, Injector, OnDestroy, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { NavigationCancel, NavigationEnd } from '@angular/router'
import { AbstractComponent, AuthenticationService, ColorThemeService, Company, CompanyHelper, currentInfoKey, EditBasicInfoCompanyComponent, EditBasicInfoUserComponent, Info, InfoComponent, InfoComponentData, InfoService, Notification, NotificationService, NotificationTypeEnum, Pager, SocketService, SoftphoneService } from 'lib-trend-core'
import { catchError, concatMap, of, Subject, takeUntil, tap } from 'rxjs'
import { ThemeService } from '../../services/theme.service'
import {
  ChatPushService,
} from 'lib-trend-core/chat'
import { UpdatePasswordComponent } from '../update-password/update-password.component'
import { AboutModalComponent } from './about-modal/about-modal.component'
import { IMPROVEMENT_RANKING_MODAL_CONFIG, ImprovementRankingModalComponent } from './improvement-ranking-modal/improvement-ranking-modal.component'
import { MOBILE_APP_MODAL_CONFIG, MobileAppModalComponent } from './mobile-app-modal/mobile-app-modal.component'
import { PlansModalComponent } from './plans-modal/plans-modal.component'

@Component({
    selector: 'header-component',
    // templateUrl: `./header.component.html`,
    templateUrl: `./header.v2.component.html`,
    styleUrls: ['../../../assets/css/core/header.scss', './header.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent extends AbstractComponent implements OnInit, OnDestroy {

  pager: Pager<Notification> = new Pager<Notification>();

  @ViewChild('userDropdownToggle') dropdownToggleRef!: ElementRef;
  @ViewChild('userDropdownMenu') dropdownMenuRef!: ElementRef;

  @Output() toggleSidenavEmitter = new EventEmitter();

  private clickListener: any;
  isDropdownMenuOpened = false;

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    const clickedInside =
      this.dropdownMenuRef?.nativeElement.contains(event.target) ||
      this.dropdownToggleRef?.nativeElement.contains(event.target);

    if (!clickedInside && this.isDropdownMenuOpened) {
      this.isDropdownMenuOpened = false;
    }
  }

  currentUser!: any;
  currentImage!: any;

  breadcrumbs: string = 'Dashboard';
  isAudioOn: boolean = true;
  // Show the "Novo" badge on the Support icon to everyone until 2026-07-20
  // (30 days after the 2026-06-20 launch); it disappears on its own afterwards.
  showSupportBadge: boolean = new Date() < new Date('2026-07-21');
  // Same for the improvements ranking icon: "Novo" until 2026-11-09 (30 days after the
  // 2026-10-10 launch).
  showImprovementBadge: boolean = new Date() < new Date('2026-11-10');

  daysRemaining: number = 0;
  expired: boolean = false;
  company: Company = this.getCompanyCurrentUser();

  constructor(
    injector: Injector,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private socketService: SocketService,
    public dialog: MatDialog,
    private renderer: Renderer2,
    private themeService: ThemeService,
    private colorTheme: ColorThemeService,
    private infoService: InfoService,
    public softphone: SoftphoneService,
    private chatPushService: ChatPushService,
  ) {
    super(injector);
    this.routingChanges();
    this.loadInfos();
  }

  ngOnInit(): void {
    this.setInitialThemeConfigs();
    this.listenToUserClickOutside();

    this.currentUser = this.getCurrentUserUser();

    this.configSocket();
    this.getNotifications();
    this.calculateTrialDays()

    this.handleSidebarCollapsedInAttendance()

    const savedAudioState = localStorage.getItem('audioState');
    if (savedAudioState !== null) {
      this.isAudioOn = JSON.parse(savedAudioState);
    }
  }

  handleSidebarCollapsedInAttendance() {
    this.themeService.getExpandedSidebar().subscribe((isExpanded) => {
      const segments = this.router.url.split('/');
      const lastSegment = segments[segments.length - 1];

      if (!isExpanded && (lastSegment === 'panel' || this.router.url.includes('/flow/edit/'))) {
        this.toggleSidenav();
      }
    });
  }

  setInitialThemeConfigs() {
    this.colorTheme.init();
  }

  override ngOnDestroy() {
    document.removeEventListener('click', this.clickListener)
  }

  listenToUserClickOutside() {
    this.clickListener = this.handleUserOutsideClick?.bind(this)
    document.addEventListener('click', this.clickListener)
  }

  toggleColorTheme(): void {
    this.colorTheme.toggle();
  }

  toggleUserDropdown(event: MouseEvent): void {
    event.preventDefault()
    event.stopPropagation()

    const toggleEl = this.dropdownToggleRef.nativeElement
    const menuEl = this.dropdownMenuRef.nativeElement

    const isOpen = toggleEl.classList.contains('show')

    if (isOpen) {
      toggleEl.classList.remove('show')
      menuEl.classList.remove('show')
    }

    toggleEl.classList.add('show')
    menuEl.classList.add('show')
  }

  toggleSidenav() {
    const width = window.innerWidth
    const sidenav = document.documentElement.getAttribute('data-sidenav-size')
    const newState = sidenav === 'default' ? 'condensed' : 'default';

    const html = document.documentElement;
    const body = document.body;

    const isSidebarEnabled = html.classList.contains('sidebar-enable');

    if (isSidebarEnabled) {
      html.classList.remove('sidebar-enable');
      body.style.overflow = '';

      const backdrop = document.getElementById('custom-backdrop');
      if (backdrop) backdrop.remove();
    } else {
      html.classList.add('menuitem-active', 'sidebar-enable');

      if (width < 768) {
        body.style.overflow = 'hidden';

        if (!document.getElementById('custom-backdrop')) {
          const backdrop = this.renderer.createElement('div');
          this.renderer.setAttribute(backdrop, 'id', 'custom-backdrop');
          this.renderer.addClass(backdrop, 'offcanvas-backdrop');
          this.renderer.addClass(backdrop, 'fade');
          this.renderer.addClass(backdrop, 'show');
          this.renderer.appendChild(body, backdrop);
        }
      }
    }

    if (width >= 768) {
      this.renderer.setAttribute(html, 'data-sidenav-size', newState);
    }

    this.toggleSidenavEmitter.emit();
  }

  toggleDropdownMenu(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDropdownMenuOpened = !this.isDropdownMenuOpened;
  }

  private handleUserOutsideClick(event: MouseEvent): void {
    if (
      !this.dropdownToggleRef?.nativeElement?.contains(event.target) &&
      !this.dropdownMenuRef?.nativeElement?.contains(event.target)
    ) {
      this.dropdownToggleRef?.nativeElement?.classList?.remove('show');
      this.dropdownMenuRef?.nativeElement?.classList?.remove('show');
    }
  }

  logout() {
    // Best-effort FCM token cleanup (T-23) — must run BEFORE the session is
    // cleared (needs the stored JWT); uses a keepalive request internally so
    // the hard redirect below cannot cancel it.
    this.chatPushService.unregisterOnLogout();
    this.authenticationService.logout();
    window.location.href = '/login';
  }

  routingChanges() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        let url = event['url'].split('/')[1]
        switch (url) {
          case 'home':
            this.breadcrumbs = 'Dashboard'
            break;
          case 'category':
            this.breadcrumbs = 'Categorias';
            break;
          case 'contact':
            this.breadcrumbs = 'Contatos';
            break;
          case 'contact-group':
            this.breadcrumbs = 'Grupo de Contatos';
            break;
          case 'department':
            this.breadcrumbs = 'Departamentos';
            break;
          case 'attendance':
            this.breadcrumbs = 'Atendimento';
            break;
          case 'tag':
            this.breadcrumbs = 'Etiquetas';
            break;
          case 'user':
            this.breadcrumbs = 'Usuários';
            break;
          case 'response':
            this.breadcrumbs = 'Respostas';
            break;
          case 'channel':
            this.breadcrumbs = 'Canais';
            break;
          case 'flow':
            this.breadcrumbs = 'Flow';
            break;
          case 'campaign':
            this.breadcrumbs = 'Campanhas';
            break;
          case 'notification':
            this.breadcrumbs = 'Notificações';
            break;
          case 'config':
            this.breadcrumbs = 'Configurações do Sistema';
            break;
          case 'integrations':
            this.breadcrumbs = 'Integrações';
            break;
          default:
            this.breadcrumbs = 'Dashboard';
            break;
        }
      }
    })
  }

  upadatePassword() {
    this.dialog.open(UpdatePasswordComponent, {
      width: '500px',
      height: '480px',
    });
  }

  private getNotifications(): void {
    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    this.notificationService.getAll(1, 7, '', this.searchParams).subscribe({
      next: (pager: Pager<Notification>) => {
        this.pager = pager;
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  toggleAudio() {
    this.isAudioOn = !this.isAudioOn;
    localStorage.setItem('audioState', JSON.stringify(this.isAudioOn));
  }

  /** Hang up the minimized call straight from the topbar pill (without re-opening the modal). */
  hangupCall(event: MouseEvent) {
    event.stopPropagation();
    this.softphone.hangup();
  }

  loadInfos(filterLocalStorage: boolean = true) {
    this.infoService.getList().subscribe((infos) => {
      const activedInfos = infos.filter((info: Info) => {
        if (filterLocalStorage) {
          const viewed = localStorage.getItem(currentInfoKey + info._id);
          if (viewed && viewed === 'true') {
            return false;
          }
        }
        return info.actived === true && info.startedAt && info.finishedAt && new Date(info.startedAt) < new Date() && new Date(info.finishedAt) > new Date();
      });
      if (activedInfos.length === 0) {
        return;
      } else {
        this.dialog.open(InfoComponent, {
          width: '900px',
          data: <InfoComponentData>{ infos: activedInfos }
        });
      }
    });
  }

  goToTaskCurrentUser() {
    const userId = this.getIDCurrentUser();
    this.router.navigate(['/task'], { queryParams: { userId } });
  }

  goToSupport() {
    this.router.navigate(['/support']);
  }

  goToBilling() {
    this.router.navigate(['/billing']);
  }

  editUserBasicInfo() {
    this.dialog.open(EditBasicInfoUserComponent, {
      width: '830px',
    });
  }

  editCompanyInfo() {
    this.dialog.open(EditBasicInfoCompanyComponent, {
      width: '1400px',
    });
  }

  goToAppV1() {
    window.open('https://trendlink.com.br/Versão-Antiga', '_blank');
  }

  openMobileAppModal() {
    this.dialog.open(MobileAppModalComponent, MOBILE_APP_MODAL_CONFIG);
  }

  openImprovementRanking() {
    this.dialog.open(ImprovementRankingModalComponent, IMPROVEMENT_RANKING_MODAL_CONFIG);
  }

  openAboutModal() {
    this.dialog.open(AboutModalComponent, {
      width: '380px',
    });
  }

  openPlansModal() {
    this.dialog.open(PlansModalComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'plans-modal-container'
    });
  }

  calculateTrialDays(): void {
    if (this.company?.trial?.activated) {
      const calculateTrialDays = CompanyHelper.calculateTrialDays(this.company.trial.startDate, this.company.trial.durationDays);
      this.daysRemaining = calculateTrialDays.daysRemaining;
      this.expired = calculateTrialDays.expired;
    }
  }

  /*********************************************************************************
   ***************************** Socket Methods - init *****************************
   *********************************************************************************/

  private notificationQueue$ = new Subject<Notification>();

  private setupNotification() {
    this.notificationQueue$.pipe(
      concatMap((notification: Notification) => of(notification).pipe(
        tap((item: Notification) => this.showNotificationToast(item)),
        catchError(() => of(null)),
      )),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private showNotificationToast(notification: Notification): void {
    const title = notification.title;
    let message = notification.description;

    const idAttendance = notification.attendance?._id;
    if (!!idAttendance) {
      const status = notification.attendance?.status ?? '';
      message += `<br><br><a href="/attendance/panel?id=${idAttendance}&status=${status}" target="_blank">${this.t('layout.header.viewAttendance')}</a>`;
    }

    if (notification.type === NotificationTypeEnum.CSAT) {
      this.alertService.error(message, title);
    } else {
      this.alertService.info(message, title);
    }
  }

  configSocket() {
    const code = super.getCodeCompanyCurrentUser();
    if (this.isAdmin() || this.isSupervisor()) {

      this.setupNotification();

      const snameEventNotification = `event_notification_${code}`;
      this.socketService.joinEvent(snameEventNotification);
      this.socketService.listen(snameEventNotification)
        .pipe(takeUntil(this.destroy$))
        .subscribe((notification: Notification) => this.notificationQueue$.next(notification));
    }
  }

  /*********************************************************************************
   ***************************** Socket Methods - end  *****************************
   *********************************************************************************/

}
